import {
  Box,
  Divider,
  Flex,
  Heading,
  Text,
  Skeleton,
  useToast,
  Link,
  HStack,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Table,
  Spacer,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import {
  useParams,
  useHistory,
  Link as ReactRouterLink,
} from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';

import {
  DeliverableContainerReceiptRecordGQLType,
  GetDeliverableContainerReceiptsDocumentGQLType,
  useCreateDeliverableContainerReceiptActionMutation,
  useGetDeliverableContainerReceiptsQuery,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { DownloadButton } from '../DownloadButton';
import { ReceiptStatusCell } from '../Status';
import {
  getDeliverableDetailViewUrl,
  getDeliverableFinalOutputReceiptsViewUrl,
} from '../utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';

const tabProps = {
  marginTop: 0,
  marginRight: 0,
  fontSize: 'sm',
  _selected: {
    color: 'blue.400',
    borderBottomColor: 'blue.400',
    borderBottomWidth: '4px',
    boxShadow: 'none',
  },
};

// moving to separate component as this will also be used in the lender view of the receipts page
export const DeliverableReceiptsInputTable: React.FC<{
  currentReceipt: DeliverableContainerReceiptRecordGQLType;
}> = ({ currentReceipt }) => {
  return (
    <>
      <Box overflowY="auto">
        <Table variant="simple">
          <Thead fontSize="md" fontWeight="bold">
            <Tr>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th>Due Date</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody fontSize="sm">
            {currentReceipt.dependenciesInstances.map((d) => {
              const fileName = `${currentReceipt.creditFacilitySlug}-${currentReceipt.containerType}-${currentReceipt.containerDueDate}-final-report-v${currentReceipt.version}-${d.artifactConfigKey}-${d.artifactReportingDate}`;
              return (
                <Tr key={d.receiptInstanceId}>
                  <Td>{commonUtils.toTitleCase(d.artifactConfigKey)}</Td>
                  <Td>{commonUtils.toTitleCase(d.artifactType)}</Td>
                  <Td>
                    {commonDate.fullDateStrToHumanDateString(
                      d.artifactReportingDate,
                    )}
                  </Td>
                  <Td>
                    <DownloadButton
                      fileName={fileName}
                      artifactInstanceId={d.receiptInstanceId}
                      iconOnly
                    />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

// moving to separate component as this will also be used in the lender view of the receipts page
export const DeliverableReceiptActionsLogTable: React.FC<{
  currentReceipt: DeliverableContainerReceiptRecordGQLType;
}> = ({ currentReceipt }) => {
  return (
    <>
      <Box overflowY="auto">
        <Table variant="simple">
          <Thead fontSize="md" fontWeight="bold">
            <Tr>
              <Th>Created At</Th>
              <Th>Action Type</Th>
              <Th>User</Th>
              <Th>Organization</Th>
              <Th>Comments</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="sm">
            {currentReceipt.actionsLog.map((a) => {
              return (
                <Tr key={a.id}>
                  <Td>{commonDate.epochToLocaleDateString(a.created_at)}</Td>
                  <Td>{commonUtils.toTitleCase(a.action)}</Td>
                  <Td>
                    {commonUtils.toTitleCase(
                      `${a.userFirstName} ${a.userLastName}`,
                    )}
                  </Td>
                  <Td>{a.organizationName}</Td>
                  <Td>{a.comments.length > 0 ? a.comments : '-'}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

const MarkReadyForReviewModal: React.FC<{
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}> = ({ isOpen, onCancel, onSubmit }) => {
  const cancelRef = React.useRef();
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef.current}
      onClose={onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Mark Ready For Review
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you want to share this report with the capital
            provider?
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef.current}
              onClick={onCancel}
              _focus={{ boxShadow: 'none' }}
              borderColor={colors.aurora[400]}
              borderWidth="1px"
              bgColor="white"
              color={colors.aurora[400]}
            >
              Cancel
            </Button>
            <Button
              colorScheme="aurora"
              onClick={onSubmit}
              ml={3}
              _focus={{ boxShadow: 'none' }}
            >
              Submit for Review
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export const DeliverableContainerReceipts: React.FC<{}> = () => {
  const history = useHistory();
  const { deliverableId, receiptId } = useParams<{
    deliverableId: string;
    receiptId: string;
  }>();
  const toast = useToast();

  finleyAssert(receiptId, 'receiptId is required');
  finleyAssert(deliverableId, 'deliverableId is required');

  const {
    data: receiptsData,
    error: receiptsError,
    refetch: refetchReceipts,
  } = useGetDeliverableContainerReceiptsQuery({
    fetchPolicy: 'network-only',
    variables: {
      containerId: deliverableId,
    },
  });

  const [
    createDeliverableContainerReceiptActionMutation,
  ] = useCreateDeliverableContainerReceiptActionMutation({
    fetchPolicy: 'network-only',
    refetchQueries: [
      {
        query: GetDeliverableContainerReceiptsDocumentGQLType,
        variables: { containerId: deliverableId },
      },
    ],
  });
  const [
    isMarkForReviewModalOpen,
    setIsMarkForReviewModalOpen,
  ] = useState<boolean>(false);

  const { data: userData } = useGetUserQuery();

  if (!receiptsData || !userData || !userData.user) {
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-final-output-report-versions-view-loading"
      />
    );
  }

  if (receiptsData.deliverableContainerReceipts.length === 0) {
    toast({ title: 'No existing versions.' });
    history.push(`${getDeliverableDetailViewUrl(deliverableId)}`);
  }

  if (receiptsError) {
    toast({ title: `Failed to load page.` });
    history.push('/deliverables');
  }

  const receipts = receiptsData.deliverableContainerReceipts;
  const currentReceipt = receipts.find((r) => r.id === receiptId);

  if (!currentReceipt) {
    toast({ title: 'Could not find version.' });
    history.push(`${getDeliverableDetailViewUrl(deliverableId)}`);
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-final-output-report-versions-view-error"
      />
    );
  }

  const {
    id: userId,
    organizationId,
    organization: { slug: orgSlug },
  } = userData.user;

  const detailViewTitle = `${commonUtils.toTitleCase(
    orgSlug,
  )} - ${commonUtils.toTitleCase(currentReceipt.containerType)}`;

  return (
    <Box height="100%">
      <Flex justify="space-between" flexDirection="column">
        <Text fontSize="xs" color="gray.800" letterSpacing="wide" pb="16px">
          <Link as={ReactRouterLink} to="/deliverables" fontWeight="semibold">
            Deliverables
          </Link>
          {' > '}
          <Link
            as={ReactRouterLink}
            to={`/deliverables/${deliverableId}`}
            fontWeight="semibold"
          >
            {detailViewTitle}
          </Link>{' '}
          {`> Report Version ${currentReceipt.version}`}
        </Text>
        <HStack pb="16px" spacing={4}>
          <Heading as="h2" fontSize="3xl">
            {commonUtils.toTitleCase(currentReceipt.creditFacilitySlug)}
          </Heading>
          <Spacer />
          <Heading as="h3" fontSize="xl">
            {`${commonUtils.toTitleCase(
              currentReceipt.containerType,
            )}, ${commonDate.fullDateStrToHumanDateString(
              currentReceipt.containerDueDate,
            )}`}
          </Heading>
        </HStack>
        <Divider mb={8} />
        <Text fontWeight="bold" fontSize="sm">
          Report Version
        </Text>
        <HStack mt={2} spacing="20px">
          <Menu>
            <MenuButton
              as={Button}
              _focus={{ boxShadow: 'none' }}
              size="sm"
              padding="20px"
              width="300px"
              textAlign="start"
              bgColor="white"
              borderWidth="1px"
              fontWeight="normal"
              rightIcon={<Icon icon="chevron-down" />}
            >
              {`Version ${currentReceipt.version}`}
            </MenuButton>
            <MenuList width="300px">
              {receipts.map((r) => {
                return (
                  <MenuItem
                    key={r.id}
                    onClick={() => {
                      // don't reload page if they click on the current receipt they are viewing
                      if (r.id !== currentReceipt.id) {
                        history.push(
                          getDeliverableFinalOutputReceiptsViewUrl({
                            containerId: deliverableId,
                            receiptId: r.id,
                          }),
                        );
                      }
                    }}
                  >{`Version ${r.version}`}</MenuItem>
                );
              })}
            </MenuList>
          </Menu>

          <ReceiptStatusCell
            mostRecentAction={currentReceipt.actionsLog[0].action}
          />

          {/* SARAH TODO: make the download button */}
          <Spacer />
          <Button
            py={5}
            px={5}
            justifyContent="flex-start"
            variant="outline"
            size="sm"
            borderColor={colors.aurora[400]}
            borderWidth="2px"
            color={colors.aurora[400]}
            _focus={{ boxShadow: 'none' }}
            leftIcon={<Icon icon="download" />}
          >
            Download Report
          </Button>

          <Button
            py={5}
            px={6}
            ml={4}
            justifyContent="flex-start"
            variant="solid"
            _focus={{ boxShadow: 'none' }}
            size="sm"
            colorScheme="aurora"
            color="white"
            isDisabled={currentReceipt.actionsLog[0].action !== 'generated'}
            onClick={() => {
              setIsMarkForReviewModalOpen(true);
            }}
          >
            Mark Ready for Review
          </Button>
        </HStack>
        <HStack mt={2} mb={2}>
          {currentReceipt.actionsLog[0].action !== 'generated' && (
            <>
              <Spacer />
              <Box maxWidth="200px">
                <Text
                  fontSize="xs"
                  color="gray.500"
                >{`Version ${currentReceipt.version} has been shared with your capital provider.`}</Text>
              </Box>
            </>
          )}
        </HStack>

        <MarkReadyForReviewModal
          isOpen={isMarkForReviewModalOpen}
          onCancel={() => {
            setIsMarkForReviewModalOpen(false);
          }}
          onSubmit={async () => {
            // mutation
            await createDeliverableContainerReceiptActionMutation({
              variables: {
                userId,
                organizationId,
                receiptId: currentReceipt.id,
                action: 'submitted',
                comments: '',
              },
            });

            // refetch receipts
            refetchReceipts();

            // close modal
            setIsMarkForReviewModalOpen(false);
          }}
        />

        <Tabs>
          <TabList>
            <Tab {...tabProps}>Inputs</Tab>
            <Tab {...tabProps}>Event Log</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <DeliverableReceiptsInputTable currentReceipt={currentReceipt} />
            </TabPanel>
            <TabPanel>
              <DeliverableReceiptActionsLogTable
                currentReceipt={currentReceipt}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Box>
  );
};
